import { useTranslation } from 'react-i18next';

import { CopyIconButton } from '~/components/common/copy-icon-button';

import { describeDeliveryError } from '../utils/delivery-error-copy';

interface Props {
  code: string;
  message: string | null | undefined;
}

/**
 * Why a run failed, on the run detail header: a readable title, the stored
 * reason (selectable, wraps, copyable for support), the next step, and the raw
 * code in small print. Old runs with no real message get "no details recorded"
 * instead of the uninformative "Activity task failed".
 */
export function RunErrorSummary({ code, message }: Props) {
  const { t } = useTranslation('deliveries');
  const { title, detail, nextStep } = describeDeliveryError(code, message, t);

  return (
    <div role="alert" className="max-w-3xl space-y-1" data-testid="delivery-run-error-summary">
      <p
        className="text-error text-sm font-semibold"
        data-testid="delivery-run-error-summary-title"
      >
        {title}
      </p>
      {detail ? (
        <div className="flex items-start gap-1">
          <p
            className="text-foreground text-sm break-words select-text"
            data-testid="delivery-run-error-summary-detail"
          >
            {detail}
          </p>
          <CopyIconButton
            text={detail}
            label={t('runError.copy')}
            className="shrink-0"
            testId="delivery-run-error-summary-copy-button"
          />
        </div>
      ) : null}
      {nextStep ? <p className="text-muted-foreground text-xs">{nextStep}</p> : null}
      <p
        className="text-muted-foreground font-mono text-[11px]"
        data-testid="delivery-run-error-summary-code"
      >
        {code}
      </p>
    </div>
  );
}
